import NumberButtons from '@/views/components/NumberButtons/NumberButtons.vue';
import ToggleSwitchButton from '@/views/components/ToggleSwitchButton/ToggleSwitchButton.vue';
import { EntrancePlantDetailDTO, EntrancePlantStructureCreateDTO, BuildingCreateDTO, PlantConfigStep, AddressingType, RFAConfigDTO, RiserConfigDTO } from '@/../client-generator/generated-clients/plants';
import { Vue, Options } from 'vue-class-component';
import plantsClient from '@/core/services/plants';
import { toLabel } from '@/core/utils/EntrancePlant/denomination-helper';
import CardContainer from '@/views/components/CardContainer/CardContainer.vue';
import { isUniqueLinkingActive } from '@/core/utils/EntrancePlant/entrance-plant.utils';

@Options({
  name: 'DialogEntrancePlantCreateSetupAddBuildings',
  props: {
    plant: {
      required: true,
      type: Object as () => EntrancePlantDetailDTO,
    },
    structureCreate: {
      required: true,
      type: Object as () => EntrancePlantStructureCreateDTO,
    },
    plantId: {
      type: Number,
      required: false,
    },   
    isEdit: {
      type: Boolean,
      default: false
    }
  },
  watch: {
    'uniqueRiserBuildingLinking'(newValue: boolean) {
      if (newValue) {
        while (this.buildings.length < this.riserCount) {
          this.addBuilding();
        }
      }
      this.disableBuildingCountChange = newValue;
    },
  },
  components: { CardContainer, NumberButtons, ToggleSwitchButton },
  emits: ['is-loading', 'onBack', 'onNext', 'plant-edited'],
})
export default class DialogEntrancePlantCreateSetupAddBuildings extends Vue {
  plant!: EntrancePlantDetailDTO;
  plantId!: number;
  structureCreate!: EntrancePlantStructureCreateDTO;
  buildings: BuildingCreateDTO[] = [];

  uniqueRiserBuildingLinking: boolean = true;
  disableBuildingCountChange: boolean = false;

  get pageTitle(): string {
    return this.$t('AppResourceEP.AddTheBuilding');
  }

  get titleCard() {
    return `${this.buildings.length} ${this.$t(
      this.buildings.length == 1
        ? 'AppResourceEP.Building'
        : 'AppResourceEP.Buildings'
    ).toString()}`;
  }

  get buildingsCount(): number {
    return this.buildings.length;
  }

  get riserCount(): number {
    const risersCount = this.structureCreate.pePlantConfig?.pePlantConfig?.risers?.length || 0;
    const hasBackbone = this.hasBackboneApartments;
    return risersCount + (hasBackbone ? 1 : 0);
  }

  get hasBackboneApartments(): boolean {
    return this.backboneApartmentsCount > 0;
  }

  get backboneApartmentsCount(): number {
    return this.structureCreate.pePlantConfig?.numbersOfBackboneApartments ?? 0;
  }

  get maxBuildingsCount(): number {
    return this.riserCount;
  }

  get canAddMoreBuildings(): boolean {
    return this.buildingsCount < this.maxBuildingsCount;
  }

  get isBRFA(): boolean {
    return this.structureCreate.pePlantConfig?.pePlantConfig?.addressingType === AddressingType.BuildingRiserFloorApartment;
  }

  addBuilding() {
    if(!this.canAddMoreBuildings) {
      return;
    }
    const building = new BuildingCreateDTO({
      name: this.getNewBuildingName(this.plant),
      apartmentsCount: 1,
    });
    
    this.buildings.push(building);
  }

  private getNewBuildingName(plant: EntrancePlantDetailDTO): string {
    let newBuildingName: string;
    let buildingIndex: number;
    let counter = 0;
    do {
      counter++;
      buildingIndex = this.buildings.length + counter;
      newBuildingName = `${this.$t('AppResourceEP.Building')} ${toLabel(
        counter,
        plant.denominationBuildings
      )}`;
    } while (this.buildings.some((x) => x.name == newBuildingName));
    return newBuildingName;
  }

  removeBuilding() {
    if (this.buildingsCount <= 0) {
      return;
    }
    this.buildings.pop();
  }

  get isNextDisabled(): boolean {
    return this.buildingsCount === 0;
  }

  mounted(): void {
    this.initializeBuildings();
  }

  initializeBuildings(): void {
    if (this.structureCreate.buildingsCreate && this.structureCreate.buildingsCreate.length > 0 
      && (this.structureCreate.pePlantConfig?.pePlantConfig?.buildings?.length ?? 0) > 0) {
      this.buildings = this.structureCreate.buildingsCreate;
      const linkingActive = isUniqueLinkingActive(this.structureCreate.pePlantConfig?.pePlantConfig!, this.hasBackboneApartments);
      // Force reactivity update
      this.uniqueRiserBuildingLinking = linkingActive;
      this.disableBuildingCountChange = linkingActive;
      this.$forceUpdate();
    }
    else
    {
      this.uniqueRiserBuildingLinking = true;
      this.disableBuildingCountChange = true;
      while (this.buildings.length < this.riserCount) {
        this.addBuilding();
      }
    }
  }

  handleBackButton(): void {
    this.$emit('onBack', 'DialogEntrancePlantCreateSetupRiser');
  }

  async handleNextButton(){
    this.$emit('is-loading', true);

    await this.updatePlantStructure(this.getDataToSave());
    
    if (this.uniqueRiserBuildingLinking) {
      await this.updatePlantStructure(this.getDataToSaveUniqueLinking());
    }

    this.$emit('is-loading', false);
    
    if (this.isBRFA) {
      this.$emit('onNext', 'DialogEntrancePlantCreateSetupBuildingBRFA');
    } else {
      const nextPage = this.uniqueRiserBuildingLinking 
        ? 'DialogEntrancePlantCreateBuildingsRecap' 
        : 'DialogEntrancePlantCreateSetupBuilding';
      this.$emit('onNext', nextPage);
    }
  }

  getDataToSave(): EntrancePlantStructureCreateDTO {
    this.structureCreate.pePlantConfig!.currentStep = PlantConfigStep.BUILDINGS_CONFIG;
    return new EntrancePlantStructureCreateDTO({
      buildingsCreate: this.buildings,
      pePlantConfig: this.structureCreate.pePlantConfig,
      riserFlow: true,
    });
  }

  getDataToSaveUniqueLinking(): EntrancePlantStructureCreateDTO {
    const updatedBuildings: BuildingCreateDTO[] = [];

    const orderedBuildings = this.structureCreate.pePlantConfig!.pePlantConfig!.buildings!.slice().sort((a, b) => a.index! - b.index!);
    const orderedRisers = this.structureCreate.pePlantConfig!.pePlantConfig!.risers!.slice().sort((a, b) => a.index! - b.index!);
    const backboneInterface = this.structureCreate.pePlantConfig!.pePlantConfig!.interfaces!.find(i => i.isBackboneInterface);
    
    for (let i = 0; i < this.buildings.length; i++) {
      const building = this.buildings[i];
      if (i == 0 && this.hasBackboneApartments && backboneInterface) {
        const updatedBuilding = new BuildingCreateDTO({
          ...building,
          buildingId: orderedBuildings[i].id,
          linkedRisersIds: [backboneInterface.id!],
        });
        updatedBuildings.push(updatedBuilding);
      } else {
        const riserIndex = this.hasBackboneApartments ? i - 1 : i;
        const riser = orderedRisers[riserIndex];
        if (riser) {
          const updatedBuilding = new BuildingCreateDTO({
            ...building,
            buildingId: orderedBuildings[i].id,
            linkedRisersIds: [riser.id!],
          });
          updatedBuildings.push(updatedBuilding);
        }
      }
    }

    const rfaConfig: {[key:string]: RFAConfigDTO} = {}
    if (this.isBRFA) {
      for (let i = 0; i < updatedBuildings.length; i++) {
        let riserConfig: RiserConfigDTO;
        if (i == 0 && this.hasBackboneApartments && backboneInterface) {
          riserConfig = new RiserConfigDTO({
            riserId: backboneInterface.id,
            index: -1,
            name: backboneInterface.name,
            floors: []
          });
        } else {
          const riserIndex = this.hasBackboneApartments ? i - 1 : i;
          riserConfig = new RiserConfigDTO({
            riserId: orderedRisers[riserIndex].id,
            index: orderedRisers[riserIndex].index,
            name: orderedRisers[riserIndex].name,
            floors: []
          });
        }

        const building = updatedBuildings[i];
        rfaConfig[building.buildingId!] = new RFAConfigDTO({
          risers: [riserConfig]
        });
      }
    }

    this.structureCreate.pePlantConfig!.currentStep = PlantConfigStep.BUILDINGS_RISERS_LINK;
    return new EntrancePlantStructureCreateDTO({
      plantConfigStructure: rfaConfig,
      buildingsCreate: updatedBuildings,
      pePlantConfig: this.structureCreate.pePlantConfig,
      riserFlow: true,
    });
  }

  private async updatePlantStructure(structure: EntrancePlantStructureCreateDTO): Promise<void> {
    if (!this.plant) return;

    try {
      const result = await plantsClient.structure(this.plant.desktopId!, structure);
      this.structureCreate.pePlantConfig = result.pePlantConfig;
      this.structureCreate.buildingsCreate = result.buildingsCreate;
    } catch {
      this.$toast.add({
        severity: 'error',
        summary: this.$t('general.operationError'),
        closable: true,
        life: 6000,
      });
      this.$emit('is-loading', false);
    }
  }
}
